import React, { useState } from "react";
import { Download, Copy, CheckCircle2, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";

/**
 * Displays 10 one-time-use backup recovery codes returned by the backend.
 * Codes are shown ONCE. After this view dismisses, the plaintext is gone forever
 * — the DB only retains SHA-256 hashes. PDF download uses jsPDF (local; no upload).
 */
export const RecoveryCodesPanel = ({ codes, userEmail, onAcknowledge, acknowledgeLabel = "I've saved my codes — continue" }) => {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const copyAll = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("All codes copied to clipboard");
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 56;
    let y = margin;
    // Header
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("ZidaMD — 2FA Backup Recovery Codes", margin, y);
    y += 28;
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Account: ${userEmail || "—"}`, margin, y); y += 16;
    doc.text(`Generated: ${new Date().toLocaleString()}`, margin, y); y += 28;
    // Warning box
    doc.setTextColor(120, 30, 30);
    doc.setFont("helvetica", "bold");
    doc.text("KEEP THESE CODES SECRET AND SECURE", margin, y); y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const warningLines = doc.splitTextToSize(
      "Each code can be used ONCE if you lose access to your authenticator app. Treat them like passwords — store in a password manager, print to a safe, or write down and lock away. Anyone with a code can bypass 2FA on your account.",
      doc.internal.pageSize.getWidth() - margin * 2,
    );
    doc.text(warningLines, margin, y);
    y += warningLines.length * 14 + 24;
    // Codes (2-column grid)
    doc.setTextColor(0, 0, 0);
    doc.setFont("courier", "bold");
    doc.setFontSize(16);
    const colWidth = (doc.internal.pageSize.getWidth() - margin * 2) / 2;
    codes.forEach((code, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = margin + col * colWidth;
      const lineY = y + row * 32;
      doc.text(`${(i + 1).toString().padStart(2, "0")}.  ${code}`, x, lineY);
    });
    y += Math.ceil(codes.length / 2) * 32 + 24;
    // Footer
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text("ZidaMD — Built to adapt. Designed to perform.  •  Generated locally, never uploaded.", margin, y);

    doc.save(`zidamd-recovery-codes-${(userEmail || "account").replace(/[^a-z0-9]/gi, "-").toLowerCase()}.pdf`);
    toast.success("Recovery codes PDF downloaded");
  };

  return (
    <div className="border border-amber-300 bg-amber-50 rounded-lg p-5 space-y-4" data-testid="recovery-codes-panel">
      <div className="flex items-start gap-3">
        <KeyRound className="w-6 h-6 text-amber-700 mt-0.5 shrink-0" />
        <div>
          <h3 className="font-semibold text-amber-900 text-base">Save your backup recovery codes</h3>
          <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
            Each code below can be used <b>once</b> if you lose access to your authenticator app. Store them in a password manager or print them. <b>You will not see these codes again.</b>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-white border border-amber-200 rounded-md p-3 font-mono text-sm" data-testid="recovery-codes-list">
        {codes.map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-brand-text" data-testid={`recovery-code-${i}`}>
            <span className="text-xs text-brand-muted w-5 text-right">{(i + 1).toString().padStart(2, "0")}.</span>
            <span className="tracking-wider select-all">{c}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={downloadPdf}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-amber-700 text-white text-sm font-medium hover:bg-amber-800"
          data-testid="recovery-codes-download-pdf"
        >
          <Download className="w-4 h-4" />
          Download as PDF
        </button>
        <button
          type="button"
          onClick={copyAll}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-md border border-amber-300 bg-white text-amber-900 text-sm font-medium hover:bg-amber-100"
          data-testid="recovery-codes-copy-all"
        >
          {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copied" : "Copy all codes"}
        </button>
      </div>

      <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer" data-testid="recovery-codes-ack-label">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-0.5"
          data-testid="recovery-codes-ack-checkbox"
        />
        <span>I have saved my recovery codes in a secure location. I understand I will not be able to view them again.</span>
      </label>

      <button
        type="button"
        onClick={onAcknowledge}
        disabled={!acknowledged}
        className="w-full px-3 py-2.5 rounded-md bg-brand-primary text-white text-sm font-semibold hover:bg-[#35096B] disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="recovery-codes-acknowledge"
      >
        {acknowledgeLabel}
      </button>

      <div className="flex items-start gap-2 text-[11px] text-amber-900/70 leading-relaxed pt-1 border-t border-amber-200">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>
          Anyone with one of these codes can sign in once without your authenticator app. Treat them like passwords.
        </span>
      </div>
    </div>
  );
};

export default RecoveryCodesPanel;
